import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Search, CheckCircle, XCircle, Loader2, ChevronDown, Navigation } from 'lucide-react';
import { siteConfig } from '../data/mock';

const LocationFinder = () => {
  const [searchMode, setSearchMode] = useState('idle'); // idle, searching, found, not_found
  const [detectedTown, setDetectedTown] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedTown, setSelectedTown] = useState('');
  const [geoError, setGeoError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null); // Debug: show user's actual coordinates
  const [nearbyTowns, setNearbyTowns] = useState([]); // Show top 3 nearest towns
  const [isApproximate, setIsApproximate] = useState(false); // true when GPS accuracy is poor (typical on PC)

  const allTowns = siteConfig.serviceArea.cities;
  const townsWithCoords = siteConfig.serviceArea.citiesWithCoords;

  // Calculate distance between two coordinates in km
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Find nearest town from user's location - returns top 5 for selection
  const findNearestTown = (userLat, userLng) => {
    const distances = [];

    Object.entries(townsWithCoords).forEach(([town, coords]) => {
      const distance = calculateDistance(userLat, userLng, coords.lat, coords.lng);
      distances.push({ name: town, distance, coords });
    });

    // Sort by distance
    distances.sort((a, b) => a.distance - b.distance);
    
    // Store top 5 for user selection (helpful on PC where GPS is inaccurate)
    setNearbyTowns(distances.slice(0, 5));
    
    // Return the nearest
    return distances[0] || null;
  };

  // Handle geolocation request
  const handleFindMyLocation = () => {
    setSearchMode('searching');
    setGeoError(null);
    setDebugInfo(null);
    setIsApproximate(false);

    if (!navigator.geolocation) {
      setGeoError("La géolocalisation n'est pas supportée par votre navigateur");
      setSearchMode('idle');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;

        // Store debug info to show user
        setDebugInfo({
          lat: latitude.toFixed(6),
          lng: longitude.toFixed(6),
          accuracy: Math.round(accuracy)
        });

        // Desktops have no GPS chip → the browser positions via Wi-Fi/IP, which is
        // often only accurate to several km (or completely wrong). The `accuracy`
        // value (in metres) tells us how much to trust the result.
        // > 1.5 km = treat as APPROXIMATE: present it as a "best guess" and push
        // the user to confirm / pick from the nearby list instead of asserting it.
        const approximate = !accuracy || accuracy > 1500;
        setIsApproximate(approximate);

        const nearest = findNearestTown(latitude, longitude);

        // When the fix is approximate, allow a wider radius so a genuine local
        // visitor whose IP resolves a few towns away still lands on the picker
        // rather than a misleading "hors zone" dead-end.
        const radius = approximate ? 60 : 35;

        if (nearest && nearest.distance <= radius) {
          setDetectedTown(nearest);
          setSearchMode('found');
        } else {
          setSearchMode('not_found');
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        if (error.code === 1) {
          setGeoError("Localisation refusée. Utilisez la recherche manuelle ci-dessous.");
        } else {
          setGeoError("Impossible de vous localiser. Utilisez la recherche manuelle.");
        }
        setSearchMode('idle');
      },
      // enableHighAccuracy is pointless on desktop (no GPS) and only adds latency/timeouts.
      // maximumAge lets us reuse a recent fix instead of re-querying every time.
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  // Handle manual town selection
  const handleTownSelect = (town) => {
    setSelectedTown(town);
    setDetectedTown({ name: town, distance: null });
    setIsApproximate(false);
    setSearchMode('found');
    setShowDropdown(false);
  };

  // Create URL-safe slug from town name
  const createSlug = (town) => {
    return town
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-lime-50 rounded-2xl p-6 md:p-8 shadow-lg border border-emerald-100">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
          <MapPin className="w-4 h-4" />
          Zone d'intervention
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Êtes-vous dans ma zone ?
        </h3>
        <p className="text-gray-600">
          J'interviens dans 31 communes autour de Coëx, Vendée
        </p>
      </div>

      {/* Main Content */}
      <div className="max-w-lg mx-auto">
        {/* Geolocation Button */}
        {searchMode === 'idle' && (
          <button
            onClick={handleFindMyLocation}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-3 text-lg transition-all shadow-md hover:shadow-lg mb-4 animate-pulse-subtle relative overflow-hidden group"
            data-testid="find-location-btn"
          >
            {/* Animated glow effect */}
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
            <Navigation className="w-6 h-6 animate-bounce-subtle" />
            📍 Trouver ma commune
          </button>
        )}

        {/* Loading State */}
        {searchMode === 'searching' && (
          <div className="bg-white rounded-xl p-6 text-center shadow-md mb-4">
            <Loader2 className="w-10 h-10 animate-spin text-emerald-600 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">Recherche de votre position...</p>
            <p className="text-sm text-gray-500 mt-1">Veuillez autoriser la géolocalisation</p>
          </div>
        )}

        {/* Found Result */}
        {searchMode === 'found' && detectedTown && (
          <div className="bg-white rounded-xl p-6 shadow-md border-2 border-emerald-500 mb-4" data-testid="location-found">
            <div className="flex items-center gap-3 mb-4">
              <div className={`${isApproximate ? 'bg-amber-100' : 'bg-emerald-100'} p-3 rounded-full`}>
                {isApproximate
                  ? <Navigation className="w-8 h-8 text-amber-600" />
                  : <CheckCircle className="w-8 h-8 text-emerald-600" />}
              </div>
              <div>
                <p className={`font-bold text-lg ${isApproximate ? 'text-amber-700' : 'text-emerald-700'}`}>
                  {isApproximate ? 'Position approximative' : 'Localisation détectée'}
                </p>
                <p className="text-gray-600">
                  {isApproximate ? 'Vérifiez et confirmez votre commune' : 'Confirmez ou corrigez votre commune'}
                </p>
              </div>
            </div>
            
            {/* Show detected town prominently */}
            <div className={`${isApproximate ? 'bg-amber-50' : 'bg-emerald-50'} rounded-lg p-4 mb-4`}>
              <p className="text-sm text-gray-500 mb-1">
                {isApproximate ? 'Commune la plus proche estimée :' : 'Commune détectée :'}
              </p>
              <p className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <MapPin className={`w-6 h-6 ${isApproximate ? 'text-amber-600' : 'text-emerald-600'}`} />
                {detectedTown.name}
              </p>
              {detectedTown.distance && !isApproximate && (
                <p className="text-sm text-gray-500 mt-1">
                  À {detectedTown.distance.toFixed(1)} km du centre
                </p>
              )}
            </div>

            {/* Show nearby alternatives - important for PC users with inaccurate GPS */}
            {nearbyTowns.length > 1 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <span>⚠️</span> Pas la bonne commune ? Choisissez ci-dessous :
                </p>
                <p className="text-xs text-amber-700 mb-3">
                  Sur ordinateur, la localisation peut être imprécise. Sur mobile, utilisez le GPS pour plus de précision.
                </p>
                <div className="space-y-2">
                  {nearbyTowns.slice(0, 5).map((town, idx) => (
                    <button
                      key={town.name}
                      onClick={() => {
                        setDetectedTown(town);
                      }}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                        detectedTown.name === town.name 
                          ? 'bg-emerald-100 border-2 border-emerald-500 text-emerald-800' 
                          : 'bg-white border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
                      }`}
                      data-testid={`nearby-town-${idx}`}
                    >
                      <span className="flex items-center gap-2">
                        <MapPin className={`w-4 h-4 ${detectedTown.name === town.name ? 'text-emerald-600' : 'text-gray-400'}`} />
                        <span className="font-medium">{town.name}</span>
                      </span>
                      <span className="text-sm text-gray-500">{town.distance.toFixed(1)} km</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Link 
              to={`/zone/${createSlug(detectedTown.name)}`}
              className="block w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-6 rounded-xl text-center transition-all mb-3"
              data-testid="view-town-page-btn"
            >
              ✓ Confirmer : {detectedTown.name} →
            </Link>

            <button
              onClick={() => { setSearchMode('idle'); setDetectedTown(null); setDebugInfo(null); setNearbyTowns([]); setIsApproximate(false); }}
              className="w-full text-gray-500 hover:text-gray-700 font-medium py-2 text-sm"
            >
              Annuler
            </button>
          </div>
        )}

        {/* Not Found Result */}
        {searchMode === 'not_found' && (
          <div className="bg-white rounded-xl p-6 shadow-md border-2 border-amber-400 mb-4" data-testid="location-not-found">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-amber-100 p-3 rounded-full">
                <XCircle className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <p className="text-amber-700 font-bold text-lg">Hors zone habituelle</p>
                <p className="text-gray-600">Mais contactez-moi pour vérifier !</p>
              </div>
            </div>
            
            <p className="text-gray-600 mb-4">
              Vous semblez être hors de ma zone de 30 km. Cependant, je peux parfois intervenir plus loin.
            </p>

            <a 
              href={`tel:${siteConfig.contact.phone}`}
              className="block w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-6 rounded-xl text-center transition-all mb-3"
            >
              📞 Appelez-moi pour vérifier
            </a>

            <button
              onClick={() => { setSearchMode('idle'); }}
              className="w-full text-gray-600 hover:text-gray-700 font-medium py-2 text-sm"
            >
              Chercher manuellement
            </button>
          </div>
        )}

        {/* Error State */}
        {geoError && (
          <div className="bg-amber-50 text-amber-800 rounded-lg p-4 mb-4 text-center">
            <p>{geoError}</p>
          </div>
        )}

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-gradient-to-br from-emerald-50 to-lime-50 text-gray-500 font-medium">
              ou recherchez votre commune
            </span>
          </div>
        </div>

        {/* Manual Search Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-full bg-white border-2 border-gray-200 hover:border-emerald-400 rounded-xl p-4 flex items-center justify-between text-left transition-all"
            data-testid="town-dropdown-btn"
          >
            <span className="flex items-center gap-2 text-gray-700">
              <Search className="w-5 h-5 text-gray-400" />
              {selectedTown || "Sélectionnez votre commune..."}
            </span>
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showDropdown && (
            <div className="absolute z-50 mt-2 w-full bg-white rounded-xl shadow-xl border border-gray-200 max-h-64 overflow-y-auto">
              {allTowns.map((town) => (
                <button
                  key={town}
                  onClick={() => handleTownSelect(town)}
                  className="w-full px-4 py-3 text-left hover:bg-emerald-50 text-gray-700 hover:text-emerald-700 border-b border-gray-100 last:border-0 flex items-center gap-2"
                  data-testid={`town-option-${createSlug(town)}`}
                >
                  <MapPin className="w-4 h-4 text-emerald-500" />
                  {town}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Link to Full Zone Page */}
        <div className="text-center mt-6">
          <Link 
            to="/zone"
            className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold"
            data-testid="view-all-zones-link"
          >
            <MapPin className="w-4 h-4" />
            Voir toutes les 31 communes →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LocationFinder;
